import React from 'react';
import PropTypes from 'prop-types';
import {Box, Typography} from '@mui/material';

/**
 * A label rendered above its input, rather than MUI's floating label.
 *
 * The app theme overrides `MuiInputLabel.shrink` with a flat
 * `translate(0, 1.5px)` to serve its own `InputBase` floating-label component
 * (see `pieces/inputs/customInputComponent.js`). That override replaces MUI's
 * `translate(14px, -9px) scale(0.75)`, so a standard outlined `TextField` label
 * never lifts clear of the outline: it renders on top of the border and, on a
 * select, on top of the value. Labels above the field sidestep the whole
 * mechanism and match how the rest of the portal presents forms.
 */
const FieldLabel = (props) => (
  <Typography
    variant={'body2'}
    sx={{mb: 0.5, fontWeight: 500, color: 'text.primary'}}
  >
    {props.label}
    {props.required && <span style={{color: 'red'}}> *</span>}
  </Typography>
);

FieldLabel.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
};

FieldLabel.defaultProps = {
  required: false,
};

/**
 * Wraps a label and its control.
 */
const Field = (props) => (
  <Box sx={{width: '100%'}}>
    <FieldLabel label={props.label} required={props.required}/>
    {props.children}
  </Box>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  children: PropTypes.node,
};

Field.defaultProps = {
  required: false,
  children: null,
};

/**
 * Restores the input padding the theme zeroes out.
 *
 * `MuiOutlinedInput.input` is overridden to `padding: '0px 12px'` for the custom
 * input component, which leaves a bare `TextField` with no vertical padding and
 * a cramped, half-height box.
 */
const inputSx = {
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-input': {
    padding: '10px 12px',
  },
  '& .MuiOutlinedInput-input.MuiInputBase-inputMultiline': {
    padding: 0,
  },
  '& .MuiOutlinedInput-root': {
    padding: '10px 12px',
  },
  '& .MuiSelect-select.MuiOutlinedInput-input': {
    padding: '10px 32px 10px 12px',
  },
};

/**
 * The autocomplete renders its own input, so it needs the padding restored at a
 * different selector depth than a plain field.
 */
const autocompleteSx = {
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': {
    padding: '2px 8px',
  },
  '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
    padding: '6px 4px',
  },
};

export {Field, FieldLabel, inputSx, autocompleteSx};
